import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  disableIntegrationForAgent, enableIntegrationForAgent, getAgentCustomTools, listDwAgents,
  type DwAgentRecord,
} from '../claudeagents/claudeAgentApi';
import { getIntegration } from './apiIntegrationService';
import './apiintegrations.css';

// An installed integration does nothing until it's turned on for a specific
// Digital Workforce agent. This is the one place that shows and changes that
// state per agent - used after install, in Installed detail, and in Browse
// detail. The Digital Workforce Integrations tab shows the same state from
// the agent's side using IntegrationSwitch and attachedToolNames below.

/** The switch every integration on/off control uses (MyTools.css mt-switch). */
export const IntegrationSwitch: React.FC<{
  on: boolean; disabled?: boolean; label: string; onToggle: () => void;
}> = ({ on, disabled, label, onToggle }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label}
    className="mt-toggle-row" style={{ marginBottom: 0 }} disabled={disabled} onClick={onToggle}>
    <span className={`mt-switch ${on ? 'mt-switch--on' : ''}`} style={{ opacity: disabled ? 0.6 : 1 }}>
      <span className="mt-switch__thumb" />
    </span>
  </button>
);

/** Tool names attached to an agent. The custom-tools endpoint's real payload is
 * {agent_id, total, attached_count, tools: [...]}, not the bare array its type declares. */
export function attachedToolNames(raw: unknown): Set<string> {
  const list: any[] = Array.isArray(raw) ? raw : Array.isArray((raw as any)?.tools) ? (raw as any).tools : [];
  return new Set(
    list
      .filter(t => t.attached === true || t.is_attached === true || t.isAttached === true)
      .map(t => t.tool_name ?? t.name)
      .filter((n): n is string => !!n),
  );
}

/** Agents an integration can be turned on for: active Claude agents (Hermes agents
 * don't run custom tools). */
export function enableableAgents(agents: DwAgentRecord[]): DwAgentRecord[] {
  return agents.filter(a => !a.is_archived && a.harness_type !== 'Hermes');
}

interface AgentRow { agent: DwAgentRecord; enabled: boolean }

export const AgentEnablementPanel: React.FC<{
  integrationId: number;
  /** The integration's slug (its chat tool name). Looked up when not given. */
  slug?: string;
}> = ({ integrationId, slug: slugProp }) => {
  const [rows, setRows] = useState<AgentRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ agentId: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    setRows(null);
    try {
      const slug = slugProp ?? (await getIntegration(integrationId)).integration.slug;
      // Full listing, not compact: compact omits harness_type, which enableableAgents needs.
      const agents = enableableAgents(await listDwAgents(false, false));
      const attached = await Promise.all(
        agents.map(a => getAgentCustomTools(a.agent_id, true).then(attachedToolNames).catch(() => new Set<string>())),
      );
      setRows(agents.map((agent, i) => ({ agent, enabled: attached[i].has(slug) })));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Couldn't load your agents.");
    }
  }, [integrationId, slugProp]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (row: AgentRow) => {
    setBusyId(row.agent.agent_id);
    setRowError(null);
    try {
      if (row.enabled) await disableIntegrationForAgent(row.agent.agent_id, integrationId);
      else await enableIntegrationForAgent(row.agent.agent_id, integrationId);
      setRows(prev => prev && prev.map(r => r.agent.agent_id === row.agent.agent_id ? { ...r, enabled: !r.enabled } : r));
    } catch (err) {
      setRowError({
        agentId: row.agent.agent_id,
        message: err instanceof Error ? err.message : "Couldn't update this agent.",
      });
    } finally {
      setBusyId(null);
    }
  };

  if (loadError) {
    return (
      <div>
        <p className="ai-error">{loadError}</p>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={load}>Try again</button>
      </div>
    );
  }
  if (rows === null) return <p className="ai-hint">Loading your agents…</p>;
  if (rows.length === 0) return <p className="ai-hint">You don't have any Digital Workforce agents yet.</p>;

  return (
    <div className="ai-agent-list">
      {rows.map(row => (
        <div key={row.agent.agent_id}>
          <div className="ai-agent-row">
            <div className="ai-agent-row__meta">
              <span>{row.agent.name}</span>
              <span className="ai-agent-row__sub">{row.enabled ? 'On - usable in chats with this agent' : 'Off'}</span>
            </div>
            <IntegrationSwitch on={row.enabled} disabled={busyId === row.agent.agent_id}
              label={`${row.enabled ? 'Turn off' : 'Turn on'} for ${row.agent.name}`} onToggle={() => toggle(row)} />
          </div>
          {rowError?.agentId === row.agent.agent_id && <p className="ai-error">{rowError.message}</p>}
        </div>
      ))}
    </div>
  );
};

/** AgentEnablementPanel in a modal, shown right after an integration is installed. */
export const AgentEnablementModal: React.FC<{
  toolName: string; integrationId: number; onClose: () => void;
}> = ({ toolName, integrationId, onClose }) => (
  <motion.div className="mt-modal-overlay"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
    onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
    <motion.div className="mt-modal"
      initial={{ opacity: 0, y: 12, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ duration: 0.2 }}>
      <div className="mt-modal__header">
        <div className="mt-modal__header-info">
          <h3 className="mt-modal__title">Turn on {toolName} for your agents</h3>
        </div>
        <button type="button" className="mt-modal__close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <p className="mt-step-subtitle">
        Installing makes this available. It shows up in a chat only for the agents you turn it on for here.
        You can change this any time from the Installed tab or the agent's Integrations tab.
      </p>
      <AgentEnablementPanel integrationId={integrationId} />
      <div className="mt-actions">
        <span />
        <button type="button" className="mt-btn mt-btn--primary" onClick={onClose}>Done</button>
      </div>
    </motion.div>
  </motion.div>
);
